import { Button } from '@components/Button';
import { IconAdd, IconGenStars } from '@components/icons';
import { PlatformIcon } from '@components/PlatformIcon';
import { useCallback, useEffect, useRef } from 'react';
import styles from './LeadsPage.module.scss';

export interface AddMenuProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onAddType: () => void;
  onAddLead: () => void;
  onAddList: () => void;
}

/**
 * Unified "+" dropdown for Generate Leads page (SC-2192).
 * Collapses the previous Add New Type + Add Lead header buttons into a
 * single outlined trigger with a two-item popover menu.
 */
export const AddMenu = ({
  isOpen,
  onOpenChange,
  onAddType,
  onAddLead,
  onAddList,
}: AddMenuProps) => {
  const menuRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onOpenChange(false);
      }
    },
    [onOpenChange]
  );

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, handleClickOutside, onOpenChange]);

  return (
    <div className={styles.addMenuWrapper} ref={menuRef}>
      <Button
        variant="secondary"
        size="small"
        leadingIcon={<IconAdd size={14} />}
        onClick={() => onOpenChange(!isOpen)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        Add
      </Button>
      {isOpen && (
        <div className={styles.addMenu} role="menu">
          <button
            type="button"
            className={styles.addMenuItem}
            role="menuitem"
            onClick={() => {
              onOpenChange(false);
              onAddType();
            }}
          >
            <IconGenStars size={18} />
            <span>Add New Type</span>
          </button>
          <button
            type="button"
            className={styles.addMenuItem}
            role="menuitem"
            onClick={() => {
              onOpenChange(false);
              onAddLead();
            }}
          >
            <IconAdd size={18} />
            <span>Add Lead</span>
          </button>
          <button
            type="button"
            className={styles.addMenuItem}
            role="menuitem"
            onClick={() => {
              onOpenChange(false);
              onAddList();
            }}
          >
            <PlatformIcon platform="apollo" className={styles.addMenuApolloIcon} />
            <span>Add List</span>
          </button>
        </div>
      )}
    </div>
  );
};
